import React, { useState } from "react";
import Modal from "react-modal";
import { Button } from "@/components/Button/Button";
import { EmailNotification } from "@/api/getEmailNotifications/types";

/**
 * Props for the Approve Notification modal.
 *  - isOpen: boolean controlling if modal is visible
 *  - onClose: callback to close the modal
 *  - onConfirm: callback to actually approve the notification
 *  - notification: the EmailNotification to be approved
 */
interface ApproveNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  notification: EmailNotification | null;
}

function ApproveNotificationModal({
  isOpen,
  onClose,
  onConfirm,
  notification,
}: ApproveNotificationModalProps) {
  const [isApproving, setIsApproving] = useState(false);

  const handleApprove = async () => {
    if (!notification) return;

    setIsApproving(true);
    try {
      await onConfirm();
    } catch (error) {
      console.error("Failed to approve notification:", error);
    } finally {
      setIsApproving(false);
    }
  };

  return (
    <Modal
      ariaHideApp={false}
      isOpen={isOpen}
      onRequestClose={onClose}
      style={{
        content: {
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          borderRadius: "8px",
          padding: "24px",
          background: "white",
          boxShadow: "0 10px 25px rgba(0,0,0,.3)",
          width: "calc(100% - 32px)",
          maxWidth: "500px",
          minBlockSize: "fit-content",
          overflowY: "auto",
        },
        overlay: {
          backgroundColor: "rgba(0,0,0,0.3)",
          zIndex: 9999,
        },
      }}
    >
      <h3 className="text-xl font-semibold text-gray-900">
        Approve Notification
      </h3>
      <p className="mt-3 text-sm text-gray-600">
        Are you sure you want to approve this notification? This action cannot
        be undone.
      </p>

      <div className="mt-6 flex justify-end space-x-3">
        <Button disabled={isApproving} onClick={onClose} variant="outline">
          Cancel
        </Button>
        <Button disabled={isApproving || !notification} onClick={handleApprove}>
          {isApproving ? "Approving..." : "Approve"}
        </Button>
      </div>
    </Modal>
  );
}

export default ApproveNotificationModal;
